// Rocky readback producer for AI Writer v2 — emits `ai-writer-screen-ready`
// schema v2 per the Rocky handoff contract (see PopAssistantButton.vue for the
// transport). Snapshots are built ONLY from what the UI itself renders:
// visible labels verbatim, IDs plus short previews — never API keys, tokens,
// prompts, raw/full HTML or complete article bodies. Collections whose detail
// lives only in child components ship with `complete: false`, which Rocky
// treats as authoritative-but-incomplete (distinct from absent).
import debounce from "lodash/debounce";
import { rockyTruncate } from "../../../rockyReadback.js";
import {
  MODES,
  PAGE_TYPES,
  SEO_INTENSITIES,
  WRITING_PRIORITIES,
  WRITING_STYLES,
  VISUAL_DENSITIES,
  VISUAL_COMPONENTS,
  IMAGE_STYLES,
} from "./constants";

export const ROCKY_SCREEN_MESSAGE_TYPE = "ai-writer-screen-ready";

// Contract bounds: ≤20 items per collection, ≤200 chars for title/block
// previews, ≤500 for other previews. rockyTruncate also strips markup.
const MAX_ITEMS = 20;
const cap = (arr) => (Array.isArray(arr) ? arr.slice(0, MAX_ITEMS) : []);
const short = (s) => rockyTruncate(s, 200);
const preview = (s) => rockyTruncate(s, 500);

// Visible labels for the two modes (ModeSelect.vue card titles, verbatim).
const MODE_OPTIONS = [
  { value: MODES.GUIDED, label: "Guided Premium" },
  { value: MODES.FAST, label: "Fast Draft" },
];
const API_MODE_OPTIONS = [
  { value: "pop", label: "Use POP credits" },
  { value: "own_key", label: "Use my OpenAI key" },
  { value: "anthropic_key", label: "Use my Claude key" },
];
const RESEARCH_OPTIONS = [
  { value: false, label: "No extra research" },
  { value: true, label: "Source support" },
];

function optionsFrom(list, selectedValue) {
  return list.map((o) => {
    const value = o.value !== undefined ? o.value : o.key;
    return { label: o.label, value, selected: value === selectedValue };
  });
}

function group(label, list, selectedValue) {
  return {
    label,
    selectedValue: selectedValue === undefined ? null : selectedValue,
    options: optionsFrom(list, selectedValue),
  };
}

function creditEstimateSummary(est) {
  if (!est) return null;
  return {
    total: est.total,
    draftCredits: est.draft_credits,
    canAfford: est.can_afford,
    breakdown: cap(est.breakdown || []).map((l) => ({
      label: short(l.label),
      credits: l.credits,
    })),
  };
}

function progressSummary(p) {
  if (!p) return null;
  return { pct: p.pct || 0, phase: p.phase || null, step: p.step || null, label: short(p.label) };
}

function outlineRows(structure) {
  const rows = (structure && structure.rows) || [];
  return cap(rows).map((r) => ({
    id: r.id,
    kind: r.kind,
    visualType: r.visual_type || null,
    label: short(r.text || r.visual_type || r.kind),
  }));
}

function modeData(vm) {
  return {
    groups: [group("Mode", MODE_OPTIONS, vm.mode)],
    status: "ready",
    loading: !!vm.creating,
    complete: true,
  };
}

function setupData(vm) {
  const setup = vm.setup || {};
  const advanced = setup.advanced || {};
  const vc = setup.visual_components || {};
  const styleSel = setup.content_style || [];
  return {
    groups: [
      group("Page type", PAGE_TYPES, setup.page_type),
      group("Visual density", VISUAL_DENSITIES, setup.visual_density),
      group("SEO intensity", SEO_INTENSITIES, setup.seo_intensity),
      group("Writing priority", WRITING_PRIORITIES, setup.writing_priority),
      {
        label: "Content style",
        selectedValue: styleSel.join(","),
        options: WRITING_STYLES.map((o) => ({ label: o.label, value: o.value, selected: styleSel.includes(o.value) })),
      },
      group("Image style", IMAGE_STYLES, setup.image_style),
      group("Research & sources", RESEARCH_OPTIONS, !!advanced.use_web_sources),
      group("AI model & credits", API_MODE_OPTIONS, advanced.api_mode || "pop"),
    ],
    toggles: VISUAL_COMPONENTS.map((c) => {
      const s = vc[c.key] || {};
      return { id: c.key, label: c.label, enabled: !!s.enabled, quantity: s.quantity != null ? s.quantity : null };
    }),
    state: {
      wordCountMode: setup.word_count_mode,
      wordCount: setup.word_count,
      contentSourceType: (setup.content_source || {}).type,
      internalLinksEnabled: !!(setup.internal_links || {}).enabled,
    },
    creditEstimate: creditEstimateSummary(vm.creditEstimate),
    status: "ready",
    loading: !!vm.saving,
    complete: true,
  };
}

function titleData(vm) {
  const t = (vm.session && vm.session.title) || {};
  const variants = t.variants || [];
  const selected = t.selected || {};
  return {
    options: cap(variants).map((v) => ({
      variantId: v.variant_id,
      seoTitle: short(v.seo_title),
      pageH1: short(v.page_h1),
      metaDescription: short(v.meta_description),
      selected: !!selected.variant_id && v.variant_id === selected.variant_id,
    })),
    state: {
      seoTitle: short(selected.seo_title),
      pageH1: short(selected.page_h1),
      metaDescription: short(selected.meta_description),
      useSameTitleAndH1:
        selected.use_same_title_and_h1 != null ? !!selected.use_same_title_and_h1
          : (selected.useSameTitleAndH1 != null ? !!selected.useSameTitleAndH1 : null),
    },
    outline: outlineRows(vm.session && vm.session.structure),
    status: vm.titleGenerating ? "generating" : (variants.length ? "ready" : "empty"),
    loading: !!vm.titleGenerating,
    complete: true,
  };
}

function draftData(vm) {
  const bs = (vm.session && vm.session.blocksSummary) || {};
  return {
    // Per-block IDs/previews live in the Draft step's own fetch; this snapshot
    // carries the summary only — flagged incomplete per the contract.
    blocks: {
      count: bs.count || 0,
      generated: !!bs.generated,
      wordCount: bs.wordCount || 0,
      items: [],
      complete: false,
    },
    outline: outlineRows(vm.session && vm.session.structure),
    // Only a live Fast Draft has a progress to report; the record left behind
    // by guided planning is not this step's progress.
    progress: vm.fastGenerating ? progressSummary(vm.liveProgress) : null,
    status: vm.fastGenerating ? "generating" : bs.generated ? "ready" : "empty",
    loading: !!vm.fastGenerating,
    complete: false,
  };
}

function seoQualityData(vm) {
  const sq = (vm.session && vm.session.seoQuality) || null;
  const scores = (sq && sq.scores) || {};
  const credits = (vm.session && vm.session.credits) || {};
  return {
    state: {
      analyzed: !!(sq && sq.analyzed),
      popScore: scores.pop_score != null ? scores.pop_score : null,
      pageExperienceScore: scores.page_experience_score != null ? scores.page_experience_score : null,
      wordCount: scores.word_count != null ? scores.word_count : null,
      issueCount: sq ? sq.issueCount : null,
      openIssueCount: sq ? sq.openIssueCount : null,
      polishStyle: sq ? sq.polishStyle : null,
      hasLastFix: !!(sq && sq.hasLastFix),
      fixRunsUsed: credits.fixRunsUsed != null ? credits.fixRunsUsed : null,
      fixRunLimit: credits.fixRunLimit != null ? credits.fixRunLimit : null,
    },
    // Per-issue IDs/labels live in the step's own fetch — summary only here.
    issues: { items: [], complete: false },
    status: sq && sq.analyzed ? "ready" : "empty",
    loading: false,
    complete: false,
  };
}

function publishData(vm) {
  const pub = (vm.session && vm.session.publish) || {};
  const meta = pub.metadata || {};
  const creditsUsed = pub.creditsUsed || {};
  const scores = (vm.session && vm.session.scores) || {};
  return {
    state: {
      seoTitle: short(meta.seo_title),
      slug: short(meta.slug),
      metaDescription: short(meta.meta_description),
      popScore: scores.popScore != null ? scores.popScore : null,
      wordCount: scores.wordCount != null ? scores.wordCount : null,
      targetWordCount: scores.targetWordCount != null ? scores.targetWordCount : null,
    },
    creditsUsed: {
      total: creditsUsed.total != null ? creditsUsed.total : null,
      lines: cap(creditsUsed.lines || []).map((l) => ({
        label: short(l.label),
        detail: short(l.detail),
        credits: l.credits,
      })),
    },
    controls: [
      { id: "publish", label: "Publish", type: "button", enabled: true },
      { id: "download", label: "Download", type: "menu", enabled: true },
      { id: "start_over", label: "Start over", type: "button", enabled: true },
    ],
    status: "ready",
    loading: false,
    complete: true,
  };
}

const STEP_BUILDERS = {
  mode: modeData,
  setup: setupData,
  title: titleData,
  draft: draftData,
  seo_quality: seoQualityData,
  publish: publishData,
};

export function buildScreenPayload(vm) {
  const siteId = vm.page && vm.page.siteId != null ? String(vm.page.siteId) : null;
  const step = vm.currentStep || "mode";
  const builder = STEP_BUILDERS[step] || modeData;
  // Generating means a job is in flight right now: Fast Draft, the guided
  // planning that bridges Setup -> Title, or title variants being written.
  // `liveProgress` is NOT a signal: pollProgress leaves it at
  // {phase: "planning", status: "complete"} after planning, which used to flag
  // every later snapshot of the session as loading (POP-0040).
  const generating = !!vm.fastGenerating || !!vm.planningBusy || !!vm.titleGenerating;
  return {
    schemaVersion: 2,
    siteId,
    pageRunId: vm.pageRunId != null ? String(vm.pageRunId) : null,
    aiRunId: (vm.session && vm.session.aiRunId) || vm.aiRunId || null,
    mode: vm.mode || null,
    currentStep: step,
    status: generating ? "generating" : "ready",
    loading: !!vm.creating || generating,
    data: builder(vm),
  };
}

// Mixin for the v2 Index: buffered emit via PopAssistantButton (replayed on
// iframe reconnect), Vuex mirror for pop-context, cleared on editor exit.
export default {
  created() {
    this._emitRockyV2Debounced = debounce(() => this.emitRockyScreenV2(), 1000);
  },
  beforeDestroy() {
    if (this._emitRockyV2Debounced) this._emitRockyV2Debounced.cancel();
    this.clearRockyScreenV2();
  },
  watch: {
    // Step change: emit a FRESH snapshot immediately (per contract, old-step
    // controls are never merged into the new step's payload).
    currentStep: {
      immediate: true,
      handler() {
        this.emitRockyScreenV2();
      },
    },
    // Session replacement (create / resume / step-save round-trips).
    session() {
      this.emitRockyScreenV2();
    },
    // Live control changes: debounced so keystrokes/toggles coalesce.
    setup: {
      deep: true,
      handler() {
        this.emitRockyScreenV2Debounced();
      },
    },
    creditEstimate() {
      this.emitRockyScreenV2Debounced();
    },
    liveProgress() {
      this.emitRockyScreenV2Debounced();
    },
    // In-flight flags: emit as soon as they flip so Rocky never keeps a
    // loading snapshot after the UI has rendered (POP-0040).
    creating() {
      this.emitRockyScreenV2();
    },
    planningBusy() {
      this.emitRockyScreenV2();
    },
    fastGenerating() {
      this.emitRockyScreenV2();
    },
    titleGenerating() {
      this.emitRockyScreenV2();
    },
  },
  methods: {
    emitRockyScreenV2() {
      const payload = buildScreenPayload(this);
      this.$store.commit("setRockyReadback", {
        screen: "aiw_v2_" + payload.currentStep,
        activeScreen: "aiw_v2_" + payload.currentStep,
        sourceType: 1, // ReportTypes.AI_DRAFT
        editorMode: "AI_MODE",
        aiRunId: payload.aiRunId,
        pageRunId: payload.pageRunId,
      });
      this.$root.$refs.popAssistantButton?.postScreenToIframe(ROCKY_SCREEN_MESSAGE_TYPE, payload);
    },
    emitRockyScreenV2Debounced() {
      if (this._emitRockyV2Debounced) this._emitRockyV2Debounced();
    },
    clearRockyScreenV2() {
      this.$root.$refs.popAssistantButton?.clearScreenFromIframe(ROCKY_SCREEN_MESSAGE_TYPE);
    },
  },
};

export { preview as rockyPreview };
